import React, { useEffect } from 'react';
import { Shape } from '../types/Shape';
import { isYoutubeUrl, extractYoutubeVideoId } from '../utils/youtubeUtils';

interface UseClipboardProps {
  editingShape: string | null;
  stagePos: { x: number; y: number };
  stageScale: number;
  width: number;
  height: number;
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  copiedShapes: Shape[]; // 복사된 객체가 있는지 확인
  selectedShapes: string[]; // 선택된 객체가 있는지 확인
  setSelectedShapes: React.Dispatch<React.SetStateAction<string[]>>;
  setShowResizeHandles: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useClipboard = (props: UseClipboardProps): void => {
  const editingShape = props.editingShape;
  const stagePos = props.stagePos;
  const stageScale = props.stageScale;
  const width = props.width;
  const height = props.height;
  const setShapes = props.setShapes;
  const copiedShapes = props.copiedShapes;
  const selectedShapes = props.selectedShapes;
  const setSelectedShapes = props.setSelectedShapes;
  const setShowResizeHandles = props.setShowResizeHandles;

  useEffect(() => {
    const handlePaste = async (e: Event) => {
      const clipboardEvent = e as ClipboardEvent;

      // input, textarea에 포커스가 있으면 무시 (세션 다이얼로그 등)
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        return;
      }

      // 텍스트 편집 중이면 무시
      if (editingShape) {
        return;
      }

      // 구글 프레젠테이션 방식:
      // - 선택된 객체가 있으면 → 복사된 객체 붙여넣기 우선 처리
      // - 선택이 해제되면 → 클립보드의 이미지/유튜브 URL 우선 처리
      if (selectedShapes.length > 0 && copiedShapes.length > 0) {
        clipboardEvent.preventDefault();

        const offset = 20;
        const newShapes = copiedShapes.map(shape => ({
          ...shape,
          id: `${Date.now()}-${Math.random()}`,
          x: shape.x + offset,
          y: shape.y + offset,
          connectedArrows: undefined,
        }));

        setShapes((prev: Shape[]) => [...prev, ...newShapes]);
        setSelectedShapes(newShapes.map(s => s.id));
        setShowResizeHandles(true);
        return;
      }

      // 선택이 해제되었거나 복사된 객체가 없을 때 클립보드의 이미지/유튜브 URL 확인
      const items = clipboardEvent.clipboardData?.items;
      let hasImage = false;
      let hasYoutubeUrl = false;

      // 먼저 클립보드에 이미지나 유튜브 URL이 있는지 확인
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          // 이미지 아이템 찾기
          if (item.type.indexOf('image') !== -1) {
            hasImage = true;
            break;
          }
        }
      }

      // 텍스트 데이터 확인 (유튜브 URL 체크)
      // 텍스트 데이터 확인 (유튜브 URL 체크)
      const textData = clipboardEvent.clipboardData?.getData('text/plain');
      let trimmedText = '';
      if (textData && textData.trim()) {
        trimmedText = textData.trim();
        if (isYoutubeUrl(trimmedText)) {
          hasYoutubeUrl = true;
        }
      }

      // 텍스트 뿐이고, 유튜브 URL이 아니라면 텍스트 객체 붙여넣기 처리
      if (!hasImage && !hasYoutubeUrl && trimmedText && selectedShapes.length === 0) {
        clipboardEvent.preventDefault();

        // 캔버스 중앙 계산
        const centerX = (-stagePos.x + width / 2) / stageScale - 100; // width/2 보정
        const centerY = (-stagePos.y + height / 2) / stageScale - 50;  // height/2 보정

        setShapes((prev: Shape[]) => {
          const maxOrder = Math.max(...prev.map((s: Shape) => s.order ?? 0), 0);
          const newOrder = maxOrder % 2 === 0 ? maxOrder + 2 : maxOrder + 1;

          const newTextShape: Shape = {
            id: Date.now().toString(),
            type: 'rect', // 사각형 기반
            x: centerX,
            y: centerY,
            width: 200,
            height: 100,
            fill: 'transparent',
            stroke: 'transparent',
            strokeWidth: 0,
            text: trimmedText, // 텍스트 내용
            label: trimmedText, // 라벨로도 저장
            fontSize: 16,
            order: newOrder,
            renderMode: 'html', // HTML 모드로 통일
          };

          // 새로 생성된 객체 선택
          setSelectedShapes([newTextShape.id]);
          setShowResizeHandles(true); // 리사이즈 핸들 표시

          return [...prev, newTextShape];
        });
        return;
      }

      // 이미지 아이템 처리
      if (hasImage && items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];

          // 이미지 아이템 찾기
          if (item.type.indexOf('image') !== -1) {
            clipboardEvent.preventDefault();
            const blob = item.getAsFile();
            if (!blob) continue;

            // Blob을 Data URL로 변환
            const reader = new FileReader();
            reader.onload = (event) => {
              const dataUrl = event.target?.result as string;
              if (!dataUrl) return;

              // 이미지 로드하여 크기 확인
              const img = new window.Image();
              img.onload = () => {
                // 캔버스 중앙에 이미지 배치
                const imgWidth = Math.min(img.width, 800);
                const imgHeight = (img.height / img.width) * imgWidth;

                const centerX = (-stagePos.x + width / 2) / stageScale - imgWidth / 2;
                const centerY = (-stagePos.y + height / 2) / stageScale - imgHeight / 2;

                // 이미지는 항상 최상위 + 짝수 order
                setShapes((prev: Shape[]) => {
                  const maxOrder = Math.max(...prev.map((s: Shape) => s.order ?? 0), 0);
                  const newOrder = maxOrder % 2 === 0 ? maxOrder + 2 : maxOrder + 1;

                  const newImageShape: Shape = {
                    id: Date.now().toString(),
                    type: 'image',
                    x: centerX,
                    y: centerY,
                    width: imgWidth,
                    height: imgHeight,
                    imageDataUrl: dataUrl,
                    stroke: '#D1D5DB',
                    strokeWidth: 1,
                    order: newOrder,
                    renderMode: 'html', // 항상 HTML 모드
                  };

                  return [...prev, newImageShape];
                });
              };
              img.src = dataUrl;
            };
            reader.readAsDataURL(blob);
            return; // 이미지 처리했으면 종료
          }
        }
      }

      // 유튜브 URL 처리
      if (hasYoutubeUrl && trimmedText) {
        if (isYoutubeUrl(trimmedText)) {
          clipboardEvent.preventDefault();
          const videoId = extractYoutubeVideoId(trimmedText);
          if (videoId) {
            // 유튜브 비디오 크기 (16:9 비율) - 화면 크기에 맞게 조정
            const baseWidth = 800;
            const baseHeight = 450;
            const videoWidth = Math.min(baseWidth, (width / stageScale) * 0.8);
            const videoHeight = (videoWidth * 9) / 16; // 16:9 비율 유지

            // 캔버스 중앙에 비디오 배치
            const centerX = (-stagePos.x + width / 2) / stageScale - videoWidth / 2;
            const centerY = (-stagePos.y + height / 2) / stageScale - videoHeight / 2;

            setShapes((prev: Shape[]) => {
              const maxOrder = prev.length > 0
                ? Math.max(...prev.map((s: Shape) => s.order ?? 0), 0)
                : 0;
              const newOrder = maxOrder % 2 === 0 ? maxOrder + 2 : maxOrder + 1;

              const newYoutubeShape: Shape = {
                id: Date.now().toString(),
                type: 'youtube',
                x: centerX,
                y: centerY,
                width: videoWidth,
                height: videoHeight,
                youtubeVideoId: videoId,
                stroke: '#FF0000', // 임시로 빨간색 테두리로 표시
                strokeWidth: 2,
                order: newOrder,
                renderMode: 'html',
              };

              return [...prev, newYoutubeShape];
            });
            return;
          }
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, [editingShape, stagePos, stageScale, width, height, setShapes, copiedShapes, selectedShapes, setSelectedShapes, setShowResizeHandles]);
};
